import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import axios from 'axios';

import vopayApi from '../../services/api.config';
import { useDataFetchIndicator } from '..';

type HookProps = {
  api: vp.Api;
};

export type PartnerInvoicePdfRequest = {
  invoiceId: string;
};

export type PartnerInvoicePdfResponse = {
  success: boolean;
  errorMessage: string;
  invoicePdf: string;
};

type ReturnTuple = [
  PartnerInvoicePdfResponse | undefined,
  (requestParams: PartnerInvoicePdfRequest) => void,
  () => void,
];

function useFetchPartnerInvoicePdf({ api }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<PartnerInvoicePdfRequest>();
  const [response, setResponse] = useState<PartnerInvoicePdfResponse>();
  const [shouldSendRequest, setSendRequest] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  useEffect(() => {
    const sendRequest = async (requestParams: PartnerInvoicePdfRequest) => {
      setDataFetchingInfo(true, 'creating pdf', 'fullScreen');

      const { Success, ErrorMessage, InvoicePDF } = await api.fetchPartnerInvoicePdf(
        buildParams(requestParams),
        source,
      );

      const res = {
        success: Success,
        errorMessage: ErrorMessage,
        invoicePdf: InvoicePDF,
      };

      setResponse(res);
      setSendRequest(false);
      setDataFetchingInfo(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams: PartnerInvoicePdfRequest) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    () => {
      setResponse(undefined);
      setSendRequest(false);
    },
  ];

  function buildParams(requestParams: PartnerInvoicePdfRequest): vp.InvoicePdfRequest {
    return {
      InvoiceID: requestParams.invoiceId,
    };
  }
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(useFetchPartnerInvoicePdf);
